import { apiClient } from "@/shared/api/client";
import type { ComputedGameAnalysis } from "./game-analyzer";
import type { AnalysisFailureReport } from "./analysis-error";

export interface UnanalyzedGame {
  id: string;
  opponent: string;
  opponentRating: number;
  playerRating: number;
  playerColor: "w" | "b";
  pgn: string;
  result: string;
  timeControl: string;
  exactTimeControl: string | null;
  playedAt: string;
  chessComAccuracy: number | null;
}

export interface AnalysisGame extends UnanalyzedGame {
  existingAnalysisDepth: number | null;
  existingAnalysisPreset: string | null;
  tacticalStatus: "verified" | "needs_reanalysis" | null;
}

export interface UploadAnalysisResult {
  saved: boolean;
}

export interface AnalysisHistoryItem {
  gameId: string;
  chessComUrl?: string | null;
  opponent: string;
  result: string;
  timeControl: string;
  playedAt: string;
  estimatedRating: number | null;
  accuracy: number | null;
  averageCpl: number;
  blunderCount: number;
  mistakeCount: number;
  imprecisionCount: number;
  criticalPositionCount: number;
  movesAnalyzed: number;
  engineNodes: number;
  analysisDepth: number;
  analysisPreset: string;
  analyzedAt: string;
  tacticalStatus: "verified" | "needs_reanalysis";
}

export interface AnalysisHistoryPage {
  items: AnalysisHistoryItem[];
  nextOffset: number | null;
}

/** Fetches games that haven't been analyzed yet */
export const fetchUnanalyzedGames = (): Promise<UnanalyzedGame[]> => apiClient<UnanalyzedGame[]>("/engine/unanalyzed");

export const fetchAnalysisGame = (gameId: string): Promise<AnalysisGame> =>
  apiClient<AnalysisGame>(`/engine/games/${gameId}`);

export const fetchAnalysisHistory = (offset = 0, limit = 40): Promise<AnalysisHistoryPage> =>
  apiClient<AnalysisHistoryPage>(`/engine/history?offset=${offset}&limit=${limit}`);

/** Uploads client-computed analysis results to the server */
export const uploadAnalysis = (
  gameId: string,
  analysis: ComputedGameAnalysis,
  analysisDepth: number,
  analysisPreset: string,
): Promise<UploadAnalysisResult> => {
  // gameId is already the URL path param — the server's request schema
  // (UploadAnalysisRequest) is deliberately strict about extra fields
  // (tsoa.json's noImplicitAdditionalProperties: "throw-on-extras"), so
  // sending analysis.gameId in the body too trips validation on every
  // upload. Strip it here rather than loosening validation server-side.
  const { gameId: analysisGameId, ...body } = analysis;
  void analysisGameId;
  return apiClient<UploadAnalysisResult>(`/engine/analysis/${gameId}`, {
    method: "POST",
    body: JSON.stringify({ ...body, analysisDepth, analysisPreset }),
  });
};

export const claimAnalysis = (gameId: string): Promise<{ claimed: true }> =>
  apiClient<{ claimed: true }>(`/engine/analysis/${gameId}/claim`, { method: "POST" });

/** Best-effort diagnostics for a final browser-worker failure. */
export const reportAnalysisFailure = (gameId: string, report: AnalysisFailureReport): Promise<{ recorded: true }> =>
  apiClient<{ recorded: true }>(`/engine/analysis/${gameId}/failure`, {
    method: "POST",
    body: JSON.stringify(report),
  });
